import { useState, useEffect, memo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Search, Edit, Trash2, Eye, RefreshCw, ChevronDown, CalendarIcon } from "lucide-react";
import { ImportExportActions, ImportMode, generateMasterDegreeTemplate } from "./ImportExportUtils";
import { MasterDegreeModal } from "./modals/MasterDegreeModal";
import { notify } from "@/lib/utils/toast";
import { masterDegreesApi, type MasterDegree, type MasterDegreeFilters } from "../lib/api";
import { useAuthStore, PERMISSIONS } from "../lib/stores/auth";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MasterDegreeDetail } from "./modals/MasterDegreeDetail";
import { format, parse } from "date-fns";
import { StatusBadge } from "./StatusBadge";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { cn } from "./ui/utils";
import { PaginationIndex } from "./PaginationIndex";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { majorCodesWithName, requestTypeList, statusList, trainingMethodList } from "./validations/shared";
import { FilterYear } from "./share/Utils";
import { AccessDenied } from "./AccessDenied";
import { NgocInput } from "./ui/ngocInput";
import { vi } from "date-fns/locale";
import { validateMasterDegreeImportData, validateMasterDegreeImportDataInDB } from "./validations/masterDegreee";
import { SmoothLoadingWrapper } from "./animate/SmoothLoadingWrapper";

export default function MasterDegreeManagement() {
	const { hasPermission } = useAuthStore();
	const [degreeInformation, setDegreeInformation] = useState<MasterDegree | null>(null);
	const [degrees, setDegrees] = useState<MasterDegree[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [requestTypeFilter, setRequestTypeFilter] = useState("");
	const [majorFilter, setMajorFilter] = useState("");
	const [trainingModeFilter, setTrainingModeFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState<any>();
	const [dateFromFilter, setDateFromFilter] = useState("");
	const [dateToFilter, setDateToFilter] = useState("");
	const [isHandleModalOpen, setHandleModalOpen] = useState(false);
	const [editingDegree, setEditingDegree] = useState<MasterDegree | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
	const [modalMode, setModalMode] = useState<"create" | "edit">("create");
	const [defenseDateFromFilterModal, setDefenseDateFromFilterModal] = useState(false);
	const [defenseDateToFilterModal, setDefenseDateToFilterModal] = useState(false);
	const [yearFromFilter, setYearFromFilter] = useState("");
	const [yearToFilter, setYearToFilter] = useState("");
	const [searchMajorName, setSearchMajorName] = useState("");
	const [isFiltering, setIsFiltering] = useState(false);
	const [autoFilters, setAutoFilters] = useState<MasterDegreeFilters>({});

	// Permission checks
	const canView = hasPermission(PERMISSIONS.masterDegrees.permissions.view.key);
	const canCreate = hasPermission(PERMISSIONS.masterDegrees.permissions.create.key);
	const canUpdate = hasPermission(PERMISSIONS.masterDegrees.permissions.update.key);
	const canDelete = hasPermission(PERMISSIONS.masterDegrees.permissions.delete.key);
	const canImport = hasPermission(PERMISSIONS.masterDegrees.permissions.import.key);
	const canExport = hasPermission(PERMISSIONS.masterDegrees.permissions.create.key);

	if (!canView) return <AccessDenied name="danh sách văn bằng thạc sĩ" />

	const fetchList = useCallback(async (customFilters?: MasterDegreeFilters) => {
		let finalFilter = customFilters || autoFilters;
		try {
			setLoading(true);

			const response = await masterDegreesApi.getAll(finalFilter);

			if (response && response.success && response.data) {
				setDegrees(response.data.data || []);
				setTotalPages(response.data.totalPages || 1);
				setTotalCount(response.data.total || 0);
			} else {
				// Fallback to empty state
				setDegrees([]);
				setTotalPages(1);
				setTotalCount(0);
			}
		} catch (error) {
			console.error("Error loading master degrees:", error);
			// Set fallback values on error
			setDegrees([]);
			setTotalPages(1);
			setTotalCount(0);
		} finally {
			setLoading(false);
		}
	}, [autoFilters]);

	useEffect(() => {
		fetchList({
			...autoFilters,
			page: currentPage,
		});
	}, [currentPage]);

	useEffect(() => {
		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	}, [itemsPerPage]);

	const [majors, setMajors] = useState<string[]>([]);

	const majorOptions: { value: string; label: string }[] = [];
	majorCodesWithName.forEach((mjcwn: { code: string; name: string }) => {
		const search = majorOptions.find((mjs) => mjs.label === mjcwn.name);
		if (!search) {
			majorOptions.push({
				value: mjcwn.name,
				label: mjcwn.name,
			});
		}
	});

	const toggleSelectMajor = (val: string) => {
		const displayData = majors.includes(val) ? majors.filter((v) => v !== val) : [...majors, val];
		setMajors(displayData);
		handleFilterChange(setMajorFilter, displayData.join(","));
	};

	const handleImport = async (data: any[], mode: ImportMode) => {
		if (!canImport) {
			notify.error("Bạn không có quyền import văn bằng");
			throw new Error("Không có quyền import");
		}

		try {
			const importedDataFromExcel: any[] = data.map((row) => ({
				majorCode: row["Mã Ngành (*)"],
				major: row["Ngành (*)"],
				cccd: row["CCCD (*)"],
				studentId: row["Mã sinh viên (*)"],
				studentName: row["Họ và tên (*)"],
				dateOfBirth: row["Ngày sinh (*)"],
				placeOfBirth: row["Nơi sinh"],
				gender: row["Giới tính (*)"],
				ethnicCode: row["Mã Dân tộc"],
				ethnicity: row["Dân tộc"],
				nationalityCode: row["Mã quốc tịch"],
				nationality: row["Quốc tịch"],
				graduationYear: row["Năm tốt nghiệp (*)"],
				trainingMode: row["Hình thức đào tạo (*)"],
				degreeNumber: row["Số hiệu bằng (cơ sở) (*)"],
				registrationNumber: row["Số vào sổ cấp bằng (*)"],
				signedBy: row["Người ký (*)"],
				signerTitle: row["Chức danh người ký (*)"],
				signDate: row["Ngày ký (*)"],
				course: row["Khoá học (*)"],
				defenseDate: row["Ngày bảo vệ (*)"],
				recognitionDecisionNumber: row["Quyết định công nhận số (*)"],
				requestTypeId: row["Loại yêu cầu ID (*)"],
				requestType: row["Loại yêu cầu (*)"],
				notes: row["Ghi chú"],
				status: row.status,
			}));

			//= Gọi hàm kiểm tra từng hàng dữ liệu có khớp với yêu cầu ràng buộc không
			let validatedImportData = validateMasterDegreeImportData(importedDataFromExcel);
			let hasErrors = validatedImportData.some((x) => x.errors.length !== 0);
			if (hasErrors) {
				return validatedImportData;
			}

			validatedImportData = await validateMasterDegreeImportDataInDB(validatedImportData);
			hasErrors = validatedImportData.some((x) => x.errors.length !== 0);
			if (hasErrors) {
				return validatedImportData;
			}

			//= Nếu không lỗi thì format lại dữ liệu rồi gửi dữ liệu import đến back-end
			const formattedImportData = validatedImportData.map((x) => {
				return {
					...x,
					dateOfBirth: format(parse(x.dateOfBirth, "dd/MM/yyyy", new Date()), "yyyy-MM-dd", { locale: vi }),
					defenseDate: format(parse(x.defenseDate, "dd/MM/yyyy", new Date()), "yyyy-MM-dd", { locale: vi }),
				};
			});

			const result = await masterDegreesApi.bulkImport(formattedImportData);

			fetchList({});
			return undefined;
		} catch (error) {
			console.error("Import error:", error);
		}
	};

	const getFullRecord = async (filter: MasterDegreeFilters = {}) => {
		const result = await masterDegreesApi.getAll({
			...filter,
			page: 1,
			limit: 999999
		});
		return result.data.data || [];
	};

	const handleExport = useCallback(async () => {
		if (!canExport) {
			notify.error("Bạn không có quyền export văn bằng");
			return [];
		}

		const exportData = await getFullRecord(isFiltering ? autoFilters : {});

		return exportData.map((row) => ({
			"Mã Ngành (*)": row.majorCode,
			"Ngành (*)": row.major,
			"CCCD (*)": row.cccd,
			"Mã sinh viên (*)": row.studentId,
			"Họ và tên (*)": row.studentName,
			"Ngày sinh (*)": row.dateOfBirth ? format(new Date(row.dateOfBirth), "dd/MM/yyyy") : "",
			"Nơi sinh": row.placeOfBirth,
			"Giới tính (*)": row.gender,
			"Mã Dân tộc": row.ethnicCode,
			"Dân tộc": row.ethnicity,
			"Mã quốc tịch": row.nationalityCode,
			"Quốc tịch": row.nationality,
			"Năm tốt nghiệp (*)": row.graduationYear,
			"Hình thức đào tạo (*)": row.trainingMode,
			"Số hiệu bằng (cơ sở) (*)": row.degreeNumber,
			"Số vào sổ cấp bằng (*)": row.registrationNumber,
			"Người ký (*)": row.signedBy,
			"Chức danh người ký (*)": row.signerTitle,
			"Ngày ký (*)": row.signDate ? format(new Date(row.signDate), "dd/MM/yyyy") : "",
			"Khoá học (*)": row.course,
			"Ngày bảo vệ (*)": row.defenseDate ? format(new Date(row.defenseDate), "dd/MM/yyyy") : "",
			"Quyết định công nhận số (*)": row.recognitionDecisionNumber,
			"Loại yêu cầu ID (*)": row.requestTypeId,
			"Loại yêu cầu (*)": row.requestType,
			"Ghi chú": row.notes,
		}));
	}, [autoFilters, isFiltering, canExport]);

	const handleExportErrors = (data: any[]) => {
		// Chuyển đổi dữ liệu có lỗi sang định dạng phù hợp để xuất file, không nên biến đổi giá trị
		return data.map((row) => ({
			"Mã Ngành (*)": row.majorCode,
			"Ngành (*)": row.major,
			"CCCD (*)": row.cccd,
			"Mã sinh viên (*)": row.studentId,
			"Họ và tên (*)": row.studentName,
			"Ngày sinh (*)": row.dateOfBirth,
			"Nơi sinh": row.placeOfBirth,
			"Giới tính (*)": row.gender,
			"Mã Dân tộc": row.ethnicCode,
			"Dân tộc": row.ethnicity,
			"Mã quốc tịch": row.nationalityCode,
			"Quốc tịch": row.nationality,
			"Năm tốt nghiệp (*)": row.graduationYear,
			"Hình thức đào tạo (*)": row.trainingMode,
			"Số hiệu bằng (cơ sở) (*)": row.degreeNumber,
			"Số vào sổ cấp bằng (*)": row.registrationNumber,
			"Người ký (*)": row.signedBy,
			"Chức danh người ký (*)": row.signerTitle,
			"Ngày ký (*)": row.signDate,
			"Khoá học (*)": row.course,
			"Ngày bảo vệ (*)": row.defenseDate,
			"Quyết định công nhận số (*)": row.recognitionDecisionNumber,
			"Loại yêu cầu ID (*)": row.requestTypeId,
			"Loại yêu cầu (*)": row.requestType,
			"Ghi chú": row.notes,
			Lỗi: row.errors,
		}));
	};

	const handleAdd = async (newDegree: Omit<MasterDegree, "id">) => {
		if (!canCreate) {
			notify.error("Bạn không có quyền thêm văn bằng thạc sĩ");
			return;
		}

		try {
			await masterDegreesApi.create(newDegree);
			notify.success("Đã thêm văn bằng thạc sĩ thành công");
			fetchList({});
		} catch (error) {
			console.error("Error adding master degree:", error);
			notify.error("Không thể thêm văn bằng thạc sĩ");
		}
	};

	const handleEdit = (degree: MasterDegree) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền chỉnh sửa văn bằng thạc sĩ");
			return;
		}
		setEditingDegree(degree);
	};

	const handleUpdate = async (updatedDegree: MasterDegree) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền cập nhật văn bằng thạc sĩ");
			return;
		}

		try {
			await masterDegreesApi.update(updatedDegree.id, updatedDegree);
			fetchList({});
			notify.success("Cập nhật văn bằng thành công");
		} catch (error) {
			console.error("Error updating master degree:", error);
		}
	};

	const handleDelete = async () => {
		if (!canDelete) {
			notify.error("Bạn không có quyền xóa văn bằng thạc sĩ");
			return;
		}
		if (deleteItemId == null) {
			notify.error("Chưa chọn văn bằng cần xóa");
			return;
		}

		try {
			await masterDegreesApi.delete(deleteItemId);
			notify.success("Xóa văn bằng thành công");

			// If we're on the last page and it becomes empty, go to previous page
			if (degrees.length === 1 && currentPage > 1) {
				fetchList();
			} else {
				fetchList({});
			}
		} catch (error) {
			console.error("Error deleting master degree:", error);
		}
	};

	const startFilters = () => {
		setIsFiltering(true);
		fetchList();
	};


	const clearFilters = () => {
		setSearchTerm("");
		setRequestTypeFilter("");
		setMajorFilter("");
		setDateFromFilter("");
		setDateToFilter("");
		setIsFiltering(false);
		setTrainingModeFilter("");
		setStatusFilter("");
		setMajors([]);
		setYearFromFilter("");
		setYearToFilter("");
		setCurrentPage(1);
		setItemsPerPage(10);
		setAutoFilters({});

		fetchList({});
	};

	const handleFilterChange = (setter: (value: string) => void, value: string) => {
		setter(value);
		setCurrentPage(1);
	};

	useEffect(() => {
		setAutoFilters({
			limit: itemsPerPage,
			search: searchTerm.trim().toLocaleLowerCase(),
			requestType: requestTypeFilter,
			major: majorFilter !== "all" ? majorFilter : undefined,
			defenseDateFrom: dateFromFilter,
			defenseDateTo: dateToFilter,
			yearFrom: yearFromFilter,
			yearTo: yearToFilter,
			trainingMode: trainingModeFilter,
			status: statusFilter,
		});
	}, [itemsPerPage, searchTerm, requestTypeFilter, majorFilter, dateFromFilter, dateToFilter, trainingModeFilter, statusFilter, yearFromFilter, yearToFilter]);


	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-2xl mb-2'>Quản Lý Văn Bằng Thạc Sĩ</h2>
					{/* <p className='text-muted-foreground'>Quản lý thông tin văn bằng thạc sĩ</p> */}
				</div>
				<div className='flex gap-2'>
					<Button variant='outline' onClick={() => fetchList()} disabled={loading}>
						<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
						Làm mới
					</Button>
					{canCreate && (
						<Button
							variant={"add"}
							onClick={() => {
								setModalMode("create");
								setHandleModalOpen(true);
							}}
						>
							<Plus className='w-4 h-4' />
							Thêm văn bằng
						</Button>
					)}
					{(canImport || canExport) && (
						<ImportExportActions
							dataType="masterDegree"
							onImport={canImport ? handleImport : async (data: any[], mode: ImportMode) => []}
							onExport={canExport ? handleExport : async () => []}
							onExportError={handleExportErrors}
							templateData={generateMasterDegreeTemplate()}
							entityName='Văn bằng Thạc sĩ'
							sampleFileName={`DSVBThS_${format(new Date(), "ddMMyyyy")}`}
							sampleErrorFileName={`DSVBThS_Errors_${format(new Date(), "ddMMyyyy")}`}
							validHeaderImport={[
								"Mã Ngành (*)",
								"Ngành (*)",
								"CCCD (*)",
								"Mã sinh viên (*)",
								"Họ và tên (*)",
								"Ngày sinh (*)",
								"Nơi sinh",
								"Giới tính (*)",
								"Mã Dân tộc",
								"Dân tộc",
								"Mã quốc tịch",
								"Quốc tịch",
								"Năm tốt nghiệp (*)",
								"Hình thức đào tạo (*)",
								"Số hiệu bằng (cơ sở) (*)",
								"Số vào sổ cấp bằng (*)",
								"Người ký (*)",
								"Chức danh người ký (*)",
								"Ngày ký (*)",
								"Khoá học (*)",
								"Ngày bảo vệ (*)",
								"Quyết định công nhận số (*)",
								"Loại yêu cầu ID (*)",
								"Loại yêu cầu (*)",
								"Ghi chú",
							]}
						/>
					)}
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className='flex items-center gap-2'>
						{/* {loading && <Loader2 className="w-4 h-4 animate-spin" />} */}
						Danh Sách Bằng Thạc Sĩ
					</CardTitle>
					<div className='flex flex-wrap items-center gap-4 mt-4'>
						<div className='relative'>
							<Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
							<NgocInput placeholder='Tìm kiếm theo Số hiệu văn bằng, tên, CCCD' value={searchTerm} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFilterChange(setSearchTerm, e.target.value)} maxLength={100} className="pl-8 min-w-96" />
						</div>
						<Select value={requestTypeFilter} onValueChange={(value) => handleFilterChange(setRequestTypeFilter, value)}>
							<SelectTrigger className='w-48'>
								<SelectValue placeholder='Loại yêu cầu' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả loại yêu cầu</SelectItem>
								{requestTypeList.map((rq) => {
									return (
										<SelectItem key={rq.code} value={rq.name}>
											{rq.name}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>
						<Select value={trainingModeFilter} onValueChange={(value) => handleFilterChange(setTrainingModeFilter, value)}>
							<SelectTrigger className='w-48'>
								<SelectValue placeholder='Hình thức đào tạo' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả hình thức đào tạo</SelectItem>
								{trainingMethodList.map((mt) => {
									return (
										<SelectItem key={mt.code} value={mt.name}>
											{mt.name}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>

						<Select value={statusFilter} onValueChange={(value) => handleFilterChange(setStatusFilter, value)}>
							<SelectTrigger className='w-48'>
								<SelectValue placeholder='Trạng thái' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả trạng thái</SelectItem>
								{statusList.map((st) => {
									return (
										<SelectItem key={st.code} value={st.name}>
											{st.name}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='outline' className={cn("w-fit justify-between text-slate-700 font-normal")}>
									{majors.length === 1 ? majorOptions.find((x) => x.value === majors[0])?.label : majors.length > 1 ? `Đã chọn ${majors.length} nghành học` : "Ngành học"}
									<ChevronDown />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<div className='flex gap-1'>
									<input
										value={searchMajorName}
										onInput={(e: any) => {
											setSearchMajorName(e.target.value);
										}}
										type='text'
										className='border w-full px-1'
										placeholder='Tìm kiếm...'
										onKeyDown={(e) => {
											e.stopPropagation();
										}}
									/>
									<button
										onClick={() => {
											setSearchMajorName("");
										}}
										type='button'
										className='cursor-pointer text-sm'
									>
										<RefreshCw size={"1rem"} />
									</button>
								</div>
								<div className='h-fit max-h-60 overflow-y-scroll'>
									{majorOptions
										.filter((mj) => mj.label.toLocaleLowerCase().includes(searchMajorName.trim().toLowerCase()))
										.map((option) => (
											<DropdownMenuCheckboxItem
												key={option.value}
												checked={majors.includes(option.value)}
												onCheckedChange={() => toggleSelectMajor(option.value)}
												className='cursor-pointer'
											>
												{option.label}
											</DropdownMenuCheckboxItem>
										))}
								</div>
							</DropdownMenuContent>
						</DropdownMenu>
						<div className='flex items-center gap-2'>
							<Popover open={defenseDateFromFilterModal} onOpenChange={setDefenseDateFromFilterModal}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{dateFromFilter ? format(dateFromFilter, "dd/MM/yyyy") : "Bảo vệ từ ngày"}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={dateFromFilter as unknown as Date}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											// localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setDateFromFilter(localDate.toISOString());
											setDefenseDateFromFilterModal(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>
							<span className='text-muted-foreground'>-</span>
							<Popover open={defenseDateToFilterModal} onOpenChange={setDefenseDateToFilterModal}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{dateToFilter ? format(dateToFilter, "dd/MM/yyyy") : "Bảo vệ đến ngày"}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={dateToFilter as unknown as Date}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											// localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setDateToFilter(localDate.toISOString());
											setDefenseDateToFilterModal(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>

							{/* <FilterDate query={dateFromFilter} placeHolder='Từ ngày' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(setDateFromFilter, e.target.value)} />
							<FilterDate query={dateToFilter} placeHolder='Đến ngày' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(setDateToFilter, e.target.value)} /> */}
						</div>
						<div className='flex items-center gap-2'>
							<FilterYear query={yearFromFilter} placeHolder='Từ năm' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(setYearFromFilter, e.target.value)} />
							<span className='text-muted-foreground'>-</span>
							<FilterYear query={yearToFilter} placeHolder='Đến năm' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(setYearToFilter, e.target.value)} />
						</div>
						<Button size={"sm"} variant='edit' onClick={() => startFilters()}>
							Lọc
						</Button>
						<Button size={"sm"} variant='outline' onClick={clearFilters}>
							Đặt lại
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{/* Table with horizontal scroll */}
					<Select
						value={itemsPerPage.toString()}
						onValueChange={(value) => {
							setItemsPerPage(Number(value));
							fetchList({
								...autoFilters,
								limit: Number(value),
								page: 1
							});
						}}
					>
						<SelectTrigger className='w-20'>
							<SelectValue placeholder='Hình thức đào tạo' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='10'>10</SelectItem>
							<SelectItem value='15'>15</SelectItem>
							<SelectItem value='20'>20</SelectItem>
							<SelectItem value='50'>50</SelectItem>
						</SelectContent>
					</Select>
					<div className='relative rounded-md border mt-2'>
						<SmoothLoadingWrapper loading={loading}>
							<Table className='min-w-[1200px]'>
								<TableHeader>
									<TableRow>
										<TableHead className='w-[120px]'>Mã sinh viên</TableHead>
										<TableHead className='w-[150px]'>Họ tên</TableHead>
										<TableHead className='w-[150px]'>Năm TN</TableHead>
										<TableHead className='w-[180px]'>Ngành</TableHead>
										<TableHead className='w-[130px]'>CCCD</TableHead>
										<TableHead className='w-[150px]'>Số hiệu bằng</TableHead>
										<TableHead className='w-[120px]'>Ngày bảo vệ</TableHead>
										<TableHead className='w-[100px]'>Hình thức</TableHead>
										<TableHead className='w-[80px]'>Khoá học</TableHead>
										<TableHead className='w-[150px]'>Trạng thái</TableHead>
										<TableHead className='w-[140px]'>Loại yêu cầu</TableHead>
										<TableHead className='w-[120px]'>Thao tác</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{
										degrees.length === 0 ? (
											<TableRow>
												<TableCell colSpan={12} className='text-center py-8 text-muted-foreground'>
													{searchTerm || isFiltering ? "Không tìm thấy kết quả phù hợp" : "Không có dữ liệu"}
												</TableCell>
											</TableRow>
										) :
											degrees.map((degree) => (
												<TableRow key={degree.id}>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.studentId}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word', 'textTransform': 'capitalize' }}>{degree.studentName}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.graduationYear}</TableCell>

													<TableCell>{degree.major}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.cccd}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.degreeNumber}</TableCell>
													<TableCell>{degree.defenseDate ? format(degree.defenseDate, "dd/MM/yyyy") : "---"}</TableCell>
													<TableCell>
														<Badge variant='outline' className='text-xs'>
															{degree.trainingMode}
														</Badge>
													</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.course}</TableCell>
													<TableCell>
														<StatusBadge name={degree.status} />
													</TableCell>
													<TableCell>
														<Badge variant='outline' className='text-xs'>
															{degree.requestType}
														</Badge>
													</TableCell>
													<TableCell>
														<div className='flex items-center gap-1'>
															<Button
																onClick={() => {
																	setDegreeInformation(degree);
																}}
																variant='ghost'
																className='h-8 w-8 p-0'
															>
																<Eye className='w-4 h-4' />
															</Button>
															{canUpdate && (
																<Button
																	variant='ghost'
																	onClick={() => {
																		setModalMode("edit");
																		handleEdit(degree);
																		setHandleModalOpen(true);
																	}}
																	className='h-8 w-8 p-0 text-yellow-600 hover:text-yellow-600'
																>
																	<Edit className='w-4 h-4' />
																</Button>
															)}
															{canDelete && (
																<Button variant='ghost' onClick={() => setDeleteItemId(degree.id)} className='h-8 w-8 p-0 text-destructive hover:text-destructive'>
																	<Trash2 className='w-4 h-4' />
																</Button>
															)}
														</div>
													</TableCell>
												</TableRow>
											))
									}
								</TableBody>
							</Table>
						</SmoothLoadingWrapper>
					</div>

					{/* Pagination */}
					{!loading && totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
				</CardContent>
			</Card>

			{/* Handle Modal */}
			{canCreate && <MasterDegreeModal isOpen={isHandleModalOpen} onClose={() => setHandleModalOpen(false)} onAdd={handleAdd} onUpdate={handleUpdate} editData={editingDegree} mode={modalMode} />}

			<MasterDegreeDetail data={degreeInformation} setDegreeInformation={setDegreeInformation} />

			<AlertDialog open={deleteItemId !== null} onOpenChange={() => setDeleteItemId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
						<AlertDialogDescription>Bạn có chắc chắn muốn xóa? Hành động này không thể hoàn tác.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Hủy</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
							Xóa
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div >
	);
}

const FilterDate = memo(({ query, onChange, placeHolder = "" }: { query: string; onChange: Function; placeHolder?: string }) => {
	return <Input type='date' placeholder={placeHolder} value={query} onChange={(e) => onChange(e)} className='w-36' />;
});
